import React from 'react';
import styled from '@emotion/styled';
import { DashboardDraft } from '../models/dashboard';
import { scoreColor, weekRange } from '../lib/dashboard';

type Props = {
  draft: DashboardDraft;
  canPublish?: boolean;
  publishing?: boolean;
  onClose: () => void;
  onPublish?: (acknowledgeWarnings: boolean) => void;
  // The client's view of this draft (DashboardPreview), opened in a new tab.
  previewHref?: string;
};

export const DashboardDraftModal = ({
  draft,
  canPublish,
  publishing,
  onClose,
  onPublish,
  previewHref,
}: Props) => {
  const blocking =
    draft.checks?.items.filter((c) => c.level === 'BLOCKING') || [];
  const warnings =
    draft.checks?.items.filter((c) => c.level === 'WARNING') || [];
  const hasBlocking = blocking.length > 0;
  const hasWarnings = warnings.length > 0;

  return (
    <Scrim
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="draft-title"
    >
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <div>
            <h2 id="draft-title">Draft Review</h2>
            <div className="subtitle">{weekRange(draft)}</div>
          </div>
          <CloseBtn aria-label="Close modal" onClick={onClose}>
            ✕
          </CloseBtn>
        </Header>

        <Body>
          {/* Summary Row */}
          <SummaryRow>
            <MetricCard>
              <div className="label">Portfolio average</div>
              <div className="value">
                {draft.summary.average !== null
                  ? `${draft.summary.average}%`
                  : '—'}
              </div>
            </MetricCard>
            <MetricCard>
              <div className="label">Reporting locations</div>
              <div className="value">
                {draft.summary.reporting} / {draft.summary.total}
              </div>
            </MetricCard>
            {draft.summary.top && (
              <MetricCard>
                <div className="label">Top location</div>
                <div className="value text-ellipsis">
                  {draft.summary.top.name}: {draft.summary.top.score}%
                </div>
              </MetricCard>
            )}
            {draft.summary.lowest && (
              <MetricCard>
                <div className="label">Lowest location</div>
                <div className="value text-ellipsis">
                  {draft.summary.lowest.name}: {draft.summary.lowest.score}%
                </div>
              </MetricCard>
            )}
          </SummaryRow>

          {previewHref && (
            <PreviewLink href={previewHref} target="_blank" rel="noreferrer">
              Preview the report as the client will see it ↗
            </PreviewLink>
          )}

          {/* Automatic Checks */}
          <SectionTitle>Automatic Pre-Publication Checks</SectionTitle>
          {hasBlocking && (
            <AlertBox className="blocking" role="alert">
              <div className="alert-title">
                ⛔ Cannot publish: {blocking.length} blocking issue(s)
              </div>
              <ul>
                {blocking.map((item, idx) => (
                  <li key={idx}>
                    <strong>[{item.code}]</strong> {item.message}
                  </li>
                ))}
              </ul>
            </AlertBox>
          )}

          {hasWarnings && (
            <AlertBox className="warning" role="alert">
              <div className="alert-title">
                ⚠️ {warnings.length} warning(s) — Reviewer acknowledgment
                required
              </div>
              <ul>
                {warnings.map((item, idx) => (
                  <li key={idx}>
                    <strong>[{item.code}]</strong> {item.message}
                  </li>
                ))}
              </ul>
            </AlertBox>
          )}

          {!hasBlocking && !hasWarnings && (
            <AlertBox className="clean">
              <div className="alert-title">
                ✓ All automated checks passed clean.
              </div>
              <p>
                No blocking issues or statistical anomalies found for this week.
              </p>
            </AlertBox>
          )}

          {/* Locations Preview */}
          <SectionTitle>Locations ({draft.locations.length})</SectionTitle>
          <LocationsList>
            {draft.locations.map((loc) => (
              <LocationRow key={loc.locationId}>
                <span className="loc-name">
                  {loc.name || `Location ${loc.locationId}`}
                </span>
                {loc.offline ? (
                  <span className="badge offline">Offline</span>
                ) : loc.score !== null ? (
                  <span
                    className="badge score"
                    style={{ color: scoreColor(loc.score) }}
                  >
                    {loc.score}%
                  </span>
                ) : (
                  <span className="badge">No score</span>
                )}
              </LocationRow>
            ))}
          </LocationsList>
        </Body>

        <Footer>
          <SecondaryButton
            type="button"
            onClick={onClose}
            disabled={publishing}
          >
            Close
          </SecondaryButton>
          {canPublish && onPublish && (
            <PrimaryButton
              type="button"
              disabled={hasBlocking || publishing}
              onClick={() => onPublish(hasWarnings)}
              className={hasWarnings ? 'btn-warn' : ''}
            >
              {publishing
                ? 'Publishing…'
                : hasBlocking
                ? 'Blocked by check errors'
                : hasWarnings
                ? 'Acknowledge Warnings & Publish'
                : 'Publish Week'}
            </PrimaryButton>
          )}
        </Footer>
      </Modal>
    </Scrim>
  );
};

export default DashboardDraftModal;

const PreviewLink = styled.a`
  display: block;
  margin: 0 0 20px;
  padding: 12px 16px;
  border: 1px solid #325882;
  border-radius: 8px;
  color: #325882;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  &:hover {
    background: #f0f4fa;
  }
`;

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(44, 49, 56, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
`;

const Modal = styled.div`
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.16);
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 28px 16px;
  border-bottom: 1px solid #e3e7eb;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #2c3138;
  }

  .subtitle {
    margin-top: 4px;
    font-size: 14px;
    color: #6e757c;
  }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: #6e757c;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background: #f4f6f8;
    color: #2c3138;
  }
`;

const Body = styled.div`
  padding: 20px 28px;
  overflow-y: auto;
  flex: 1;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const MetricCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e3e7eb;
  border-radius: 8px;
  padding: 12px 14px;

  .label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: #6e757c;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }

  .value {
    font-size: 18px;
    font-weight: 700;
    color: #2c3138;
  }

  .text-ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #5a606a;
  margin: 16px 0 10px;
`;

const AlertBox = styled.div`
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 16px;
  font-size: 13px;

  .alert-title {
    font-weight: 600;
    margin-bottom: 6px;
  }

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-top: 4px;
  }

  p {
    margin: 0;
  }

  &.blocking {
    background: #fdf2f2;
    border: 1px solid #f8b4b4;
    color: #9b1c1c;
  }

  &.warning {
    background: #fdf8eb;
    border: 1px solid #fce3a7;
    color: #92400e;
  }

  &.clean {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
  }
`;

const LocationsList = styled.div`
  border: 1px solid #e3e7eb;
  border-radius: 8px;
  max-height: 180px;
  overflow-y: auto;
  background: #ffffff;
`;

const LocationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  border-bottom: 1px solid #f1f3f5;

  &:last-child {
    border-bottom: none;
  }

  .loc-name {
    font-size: 13px;
    color: #2c3138;
    font-weight: 500;
  }

  .badge {
    font-size: 12px;
    font-weight: 600;

    &.score {
      font-size: 13px;
    }

    &.offline {
      color: #cf3320;
      background: rgba(232, 65, 44, 0.1);
      padding: 2px 8px;
      border-radius: 4px;
    }
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 28px;
  border-top: 1px solid #e3e7eb;
  background: #f8fafc;
`;

const SecondaryButton = styled.button`
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f9fafb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  background: #325882;
  border: none;
  border-radius: 6px;
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #274669;
  }

  &.btn-warn {
    background: #b45309;

    &:hover:not(:disabled) {
      background: #92400e;
    }
  }

  &:disabled {
    background: #98a2b3;
    cursor: not-allowed;
  }
`;
